/**
 * TEMA — Média Móvel Exponencial Tripla (Triple Exponential Moving Average).
 *
 * Migrado do Silverlight (GraficoDePrata/Formulas/FMLMovingAverages.cs, classe
 * FML.TEMA):
 *
 *   TEMA = 3·EMA(C,N) − 3·EMA(EMA(C,N),N) + EMA(EMA(EMA(C,N),N),N)
 *
 * EMA(f,N) = DMA(f, 2/(N+1)) com seed out[0]=f[0] (silverlightDsl) — NÃO é o
 * `ema` do odin (d3fc, seed = SMA dos N primeiros e undefined no warm-up); por
 * isso o porte usa o primitivo do DSL para bater bit-a-bit com o oráculo. O
 * engine emite valores desde a barra 0 (sem warm-up NaN). Paridade provada em
 * __tests__/tema.silverlight.test.js.
 *
 * O DESLOCAR do engine NÃO é tratado aqui: virou o `offsetBars` genérico,
 * aplicado uma única vez em `applyIndicators` (`utils/calculators.js`) via
 * `offsetSeries`. Deslocar a saída equivale ao `REF` na entrada para um cálculo
 * causal e invariante a deslocamento, que é o caso do TEMA — e assim não há dois
 * mecanismos podendo aplicar o mesmo deslocamento duas vezes.
 */
import { getSourcePathAccessor } from "../utils";
import { ema, mul, sub, add } from "./silverlightDsl";
import { TEMA as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { windowSize, sourcePath } = options;

    const source = getSourcePathAccessor(sourcePath);
    const c = data.map((d, i) => {
      const v = source(d, i);
      return Number.isFinite(v) ? v : NaN;
    });

    const e1 = ema(c, windowSize);
    const e2 = ema(e1, windowSize);
    const e3 = ema(e2, windowSize);
    // 3·EMA1 − 3·EMA2 + EMA3 — mesma ordem de operações do engine (3D*e1 - 3D*e2 + e3).
    const tema = add(sub(mul(3, e1), mul(3, e2)), e3);

    // Borda com o chart: NaN (dado ausente) vira undefined.
    return tema.map((v) => (Number.isFinite(v) ? v : undefined));
  }

  calculator.undefinedLength = function () {
    // O engine emite desde a barra 0 (sem warm-up NaN).
    return 0;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
